import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { GoogleMap, useLoadScript } from '@react-google-maps/api'
import { focusMapToPlace, googleAPIKey, isZeroLatLng } from 'lib/google'
import { useDeepCompareEffect, useGeolocation } from 'react-use'
import { withTranslationCustom } from 'lib/i18next'
import { useDebouncedCallback } from 'use-debounce'

import ErrorComponent from 'components/Errors/ErrorComponent'
import MapsSearchBox from './MapsSearchBox'
import MapsNearbyPlaces from './MapsNearbyPlaces'
import MapsSelectedPlace from './MapsSelectedPlace'
import MapCenterMarker from './MapCenterMarker/MapCenterMarker'
import MapsPlaceSelectHeader from './MapPlaceSelectHeader/MapsPlaceSelectHeader'

import './maps-place-select.pcss'

const API_KEY = googleAPIKey()

const LIBRARIES = ['places']
// const FIELDS = ['name', 'formatted_address', 'geometry']

const geolocationKamppi = { lat: 60.16638113313444, lng: 24.93215740000005 }
const zoomOnGeolocation = 12
const zoomNoGeolocation = 9

const MapContainer = ({ children, onCloseRequest, loading }) => {
    return (
// eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
            id="maps-place-select"
            className="maps-place-select"
            onClick={(e) => (
                // backdrop click
                e.target.id === 'maps-place-select' && onCloseRequest()
            )}
        >
            <MapsPlaceSelectHeader onCloseRequest={onCloseRequest} />
            <div
                className={classNames('maps-place-select__map-container', {
                    loading: loading,
                })}
            >
                {children}
            </div>
        </div>
    )
}

const MapsPlaceSelect = ({
    i18n,
    types,
    onPlaceSelect,
    onCloseRequest,
    initCenter,
    initPlaceId,
}) => {
    const [selectedPlace, setSelectedPlace] = useState(null)
    const [selectedPlaceId, setSelectedPlaceId] = useState(initPlaceId)
    const [center, setCenter] = useState(initCenter)
    const [map, setMap] = useState(null)
    const [bounds, setBounds] = useState(null)

    useEffect(() => {
        const openClassName = 'MapsPlaceSelect--open'
        document.body.classList.add(openClassName)
        return () => document.body.classList.remove(openClassName)
    }, [])

    const [updateMapCenter] = useDebouncedCallback(() => {
        setCenter({
            lat: map.getCenter().lat(),
            lng: map.getCenter().lng(),
        })
    }, 1000)

    useDeepCompareEffect(() => {
        if (map) {
            map.setCenter(center)
        }
    }, [center])

    const result = useLoadScript({
        googleMapsApiKey: API_KEY,
        libraries: LIBRARIES,
        language: i18n.language,
        region: 'FI',
    })

    const { isLoaded, loadError } = result

    const geolocation = useGeolocation({
        timeout: 20000,
    })

    if (loadError) {
        return (
            <MapContainer onCloseRequest={onCloseRequest}>
                <ErrorComponent error={loadError} />
            </MapContainer>
        )
    }
    if (geolocation.loading || !isLoaded) {
        return (
            <MapContainer loading={true} onCloseRequest={onCloseRequest} />
        )
    }

    const useGeo = geolocation.latitude && geolocation.longitude

    const zoom = useGeo ? zoomOnGeolocation : zoomNoGeolocation

    const defaultCenter = useGeo ? {
        lat: geolocation.latitude,
        lng: geolocation.longitude,
    } : geolocationKamppi

    const handlePickPlace = (place) => {
        setSelectedPlace(place)
        setSelectedPlaceId(place.place_id)
        focusMapToPlace(map, place)
    }

    const handleCenterChange = () => {
        if (map) updateMapCenter()
    }

    const handleNearbyPlacesChange = () => {}

    const handleMapClick = (e) => {
        if (e.placeId) {
            e.stop() // prevent google maps from displaying default info bubble

            setCenter({
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
            })
            setSelectedPlace(null)
            setSelectedPlaceId(e.placeId)
        }
    }

    const handleMapDoubleClick = (e) => {
        setSelectedPlace(null)
        setSelectedPlaceId(null)
    }

    const handleDragEnd = (e) => {
        setSelectedPlace(null)
        setSelectedPlaceId(null)
    }

    const mapCenter = !isZeroLatLng(center) ? center : defaultCenter

    return (
        <MapContainer onCloseRequest={onCloseRequest}>
            <GoogleMap
                id="maps-place-select-map"
                mapContainerStyle={{ width: '100%', height: '300px' }}
                zoom={zoom}
                onClick={handleMapClick}
                onDblClick={handleMapDoubleClick}
                onLoad={map => {
                    setMap(map)
                    map.setCenter(mapCenter)
                }}
                onBoundsChanged={() => setBounds(map.getBounds())}
                onUnmount={map => null}
                onCenterChanged={handleCenterChange}
                onDragEnd={handleDragEnd}
                options={{
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                }}
            >
                {!!map && ( // wait for map to be accessible right away for sub components to simplify things
                    ReactDOM.createPortal(
                        (
                            <>
                                <div className="maps-place-select__overlay-components">
                                    <MapCenterMarker />
                                    <div className="maps-place-select__search-container">
                                        <MapsSearchBox
                                            onPlaceSelect={handlePickPlace}
                                            bounds={bounds}
                                        />
                                    </div>
                                </div>
                                <div className="maps-place-select__components">
                                    <div className="maps-place-select__select-container">
                                        <MapsSelectedPlace
                                            point={mapCenter}
                                            place={selectedPlace}
                                            placeId={selectedPlaceId}
                                            onSelect={onPlaceSelect}
                                        />
                                        <MapsNearbyPlaces
                                            types={types}
                                            center={mapCenter}
                                            onPlaceSelect={handlePickPlace}
                                            onPlacesChange={handleNearbyPlacesChange}
                                        />
                                    </div>
                                </div>
                            </>

                        ),
                        document.getElementById('maps-place-select-components-container')
                    )
                )}
            </GoogleMap>
            <div id="maps-place-select-components-container" />
        </MapContainer>
    )
}

MapsPlaceSelect.propTypes = {
    i18n: PropTypes.object,
    types: PropTypes.array.isRequired,
    initCenter: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
    }).isRequired,
    onPlaceSelect: PropTypes.func.isRequired,
    onCloseRequest: PropTypes.func.isRequired,
}

MapsPlaceSelect.defaultProps = {
    initCenter: { lat: 0, lng: 0 },
}

export default withTranslationCustom('common')(MapsPlaceSelect)
